import React, { createContext, useCallback, useContext, useState, useEffect, ReactNode } from 'react';
import { api, REFRESH_TOKEN_KEY, resetSessionExpirySignal } from './ApiContext';
import { accessTokenIsExpired } from '../utils/accessTokenExpiry';
import { SESSION_EXPIRED_EVENT } from '../utils/httpClientErrorMessage';

const USER_STORAGE_KEY = 'user';

function normalizeAuthUser(payload: Partial<User>): User {
  return {
    ...payload,
    tx_import: Boolean(payload.tx_import),
    manage_rules: Boolean(payload.manage_rules),
    manual_balances: Boolean(payload.manual_balances),
  } as User;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'owner' | 'admin' | 'user' | 'guest';
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
  // RBAC Permissions
  can_view_properties: boolean;
  can_view_balances: boolean;
  can_view_calendar: boolean;
  can_view_entities: boolean;
  can_manage_families: boolean;
  /** Interactive Revolut/Pandle (etc.) ledger import mutations */
  tx_import: boolean;
  /** Rule catalogue edits (classification rules, VAT/alises where gated) */
  manage_rules: boolean;
  manual_balances: boolean;
  /** Personal Finances household tabs this user may open (`seco` and/or `omhe`). */
  personal_households?: Array<'seco' | 'omhe'>;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const clearAuthStorage = () => {
      localStorage.removeItem('token');
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      setToken(null);
    };

    const refreshUserFromApi = async (storedToken: string) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 75000); // 75s for cold start
      try {
        const response = await api.get('/api/auth/me', { signal: controller.signal });
        const userData = normalizeAuthUser(response.data.user);
        setUser(userData);
        setToken(storedToken);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
        return 'ok' as const;
      } catch (error: any) {
        if (error.response?.status === 401) {
          console.log('Token invalid (401), clearing auth');
          clearAuthStorage();
          return 'unauthorized' as const;
        }
        // Network/timeout – backend likely cold/overloaded
        return 'unavailable' as const;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      if (accessTokenIsExpired(storedToken) && !localStorage.getItem(REFRESH_TOKEN_KEY)) {
        clearAuthStorage();
        setLoading(false);
        return;
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

      // Optimistic session: paint from cache immediately so hard refresh is not
      // blocked by a slow /api/auth/me (API overload / single worker).
      const cachedRaw = localStorage.getItem(USER_STORAGE_KEY);
      if (cachedRaw) {
        try {
          const cachedUser = normalizeAuthUser(JSON.parse(cachedRaw) as Partial<User>);
          setUser(cachedUser);
          setToken(storedToken);
          setLoading(false);
          const status = await refreshUserFromApi(storedToken);
          if (status === 'unavailable') {
            console.log('Backend unavailable, using cached user – will refresh when backend is ready');
            const refreshWhenReady = async () => {
              await new Promise((r) => setTimeout(r, 15000));
              try {
                const r = await api.get('/api/auth/me', { timeout: 60000 });
                const fresh = normalizeAuthUser(r.data.user);
                setUser(fresh);
                localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
              } catch {
                // Ignore – user stays on cached data
              }
            };
            void refreshWhenReady();
          }
          return;
        } catch {
          console.log('Could not parse cached user');
        }
      }

      // No usable cache – must wait for /me (or fail closed to login).
      const status = await refreshUserFromApi(storedToken);
      if (status === 'unavailable') {
        // Keep token for later retries but stay logged out until /me succeeds.
      }
      setLoading(false);
    };

    void initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login for:', email);
      
      let response;
      let retries = 4;
      let delay = 3000;
      
      while (retries > 0) {
        try {
          response = await api.post('/api/auth/login', { email, password }, {
            timeout: 90000
          });
          break;
        } catch (error: any) {
          const isRetryable = 
            error.code === 'ECONNABORTED' || // Timeout
            error.code === 'ERR_CANCELED' || // Request canceled (e.g. browser/axios)
            error.code === 'ERR_NETWORK' || // Network error (includes CORS errors)
            error.message?.includes('CORS') || // Explicit CORS error detection
            !error.response || // No response (backend down)
            error.response?.status === 503 || // Service unavailable
            error.response?.status === 502; // Bad gateway
          
          if (isRetryable && retries > 1) {
            // Retry with exponential backoff, but cap at 15 seconds
            const errorType = error.response?.status 
              ? `HTTP ${error.response.status}` 
              : error.code === 'ERR_NETWORK' 
                ? 'Network/CORS error' 
                : error.code || 'Unknown error';
            
            console.log(`Login attempt failed (${errorType}), retrying in ${delay/1000}s... (${retries - 1} attempts left)`);
            retries--;
            await new Promise(resolve => setTimeout(resolve, delay));
            delay = Math.min(delay * 1.3, 15000); // Increase delay, max 15 seconds
            continue;
          }
          throw error;
        }
      }
      
      if (!response) {
        throw new Error('Login failed. Could not reach the server. Check that the API is running and try again.');
      }
      
      const { user: userData, token: newToken, refresh_token: refreshToken } = response.data;
      const normalized = normalizeAuthUser(userData);

      setUser(normalized);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalized));
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      resetSessionExpirySignal();

      console.log('Login successful for:', email);
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.status === 503 || error.response?.status === 502) {
        throw new Error('Backend service is temporarily unavailable. Try again in a moment.');
      } else if (error.code === 'ERR_NETWORK' || !error.response || error.message?.includes('CORS')) {
        throw new Error('Cannot connect to server. Check that the API is running and try again.');
      } else {
        throw new Error(error.response?.data?.error || error.message || 'Login failed');
      }
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName
      });
      const { user: userData, token: newToken, refresh_token: refreshToken } = response.data;
      const normalized = normalizeAuthUser(userData);

      setUser(normalized);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      }
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalized));
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      resetSessionExpirySignal();
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    delete api.defaults.headers.common['Authorization'];
  }, []);

  useEffect(() => {
    const onExpired = () => logout();
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [logout]);

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};